import React from 'react';

import { getIsWorkflowListGroupableDimension } from '@aline/common';
import { Typography } from '@mui/material';
import type { WorkflowListGridRow } from 'src/components/workflow/workflow-list/WorkflowsList';
import { getIsWorkflowListGroupRow } from 'src/components/workflow/workflow-list/WorkflowsList';

/** MUI's field for the single grouping column, which it does not export publicly. */
export const WORKFLOW_LIST_GROUPING_FIELD = '__row_group_by_columns_group__';

type WorkflowListGroupCellParams = {
  field: string;
  row: WorkflowListGridRow;
};

/**
 * The grouping column renders its cells through the grouped-by column's `renderCell`,
 * and those cells are editable pickers that only make sense for a Workflow row. Group
 * headers show the server's bucket label there, and nothing in their other columns.
 */
export function WorkflowListGroupLabelCell({
  count,
  field,
  label,
}: {
  count: number;
  field: string;
  label: string;
}): JSX.Element | null {
  if (field !== WORKFLOW_LIST_GROUPING_FIELD) return null;

  return (
    <Typography noWrap variant="body2">
      {label} ({count})
    </Typography>
  );
}

export function renderWorkflowListGroupCell(
  params: WorkflowListGroupCellParams
): JSX.Element | null {
  if (!getIsWorkflowListGroupRow(params.row) || params.field !== WORKFLOW_LIST_GROUPING_FIELD)
    return null;

  return (
    <WorkflowListGroupLabelCell
      count={params.row.workflowCount}
      field={params.field}
      label={params.row.groupLabel}
    />
  );
}

function getHasWorkflowListColumnRenderCell(column: { renderCell?: unknown }): column is {
  renderCell: (params: WorkflowListGroupCellParams) => React.ReactNode;
} {
  return typeof column.renderCell === 'function';
}

/**
 * MUI paints the Group-by column with the grouped field's `renderCell`. Any
 * groupable column that supplies one has to show the bucket label on group
 * rows or that cell stays blank.
 */
export function withWorkflowListGroupCell<TParams extends WorkflowListGroupCellParams>(
  renderWorkflow: (params: TParams) => React.ReactNode
): (params: TParams) => React.ReactNode {
  return params => renderWorkflowListGroupCell(params) ?? renderWorkflow(params);
}

export function getWorkflowListGroupAwareRenderCell(column: {
  field: string;
  renderCell?: unknown;
}): ((params: WorkflowListGroupCellParams) => React.ReactNode) | undefined {
  if (!getHasWorkflowListColumnRenderCell(column)) return undefined;
  if (!getIsWorkflowListGroupableDimension(column.field)) return column.renderCell;
  return withWorkflowListGroupCell(column.renderCell);
}
